import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Lightning } from "@phosphor-icons/react";
import { blockerRows } from "@/lib/blockers";
import { InstallBadge, projectInstall } from "@/pages/Projects";
import { HoldBadge } from "@/components/HoldBadge";

const SHOW = 6;

const stepHref = (p, s) => {
  const g = s.go || {};
  if (g.tab) return `/projects/${p.id}?tab=${g.tab}`;
  if (g.action === "invoice" && g.id) return `/invoices/${g.id}`;
  if (g.action === "customer" && p.customer_id) return `/customers/${p.customer_id}`;
  return `/projects/${p.id}`;
};

// Every active project's blocking next steps — soonest install first, then most blocked.
// `compact` is the dashboard widget (top 3, one line each); `full` lists everything.
export const ProjectBlockersFeed = ({ projects = [], conflicts = [], loading, compact = false, full = false }) => {
  const [showAll, setShowAll] = useState(false);
  const rows = blockerRows(projects, conflicts).sort((a, b) => {
    const da = projectInstall(a.p)?.days ?? 9999;
    const db = projectInstall(b.p)?.days ?? 9999;
    if (da !== db) return da - db;
    return b.blocks.length - a.blocks.length;
  });
  const total = rows.reduce((n, r) => n + r.blocks.length, 0);
  const visible = showAll || full ? rows : rows.slice(0, SHOW);

  if (compact) {
    return (
      <section data-testid="dash-blockers" className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <Lightning size={18} weight="fill" className="text-amber-300" />
          <h2 className="text-base font-bold text-zinc-100 md:text-lg">Needs a push</h2>
          {!loading && total > 0 && (
            <span className="font-mono-data rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-black text-white" data-testid="dash-blockers-count">
              {total}
            </span>
          )}
          <span data-testid="dash-blockers-summary" className="text-xs text-zinc-500">
            {loading
              ? "Checking projects…"
              : rows.length
                ? `${total} blocker${total === 1 ? "" : "s"} across ${rows.length} project${rows.length === 1 ? "" : "s"}`
                : "Every active project is moving"}
          </span>
          <Link
            to="/needs-a-push"
            data-testid="dash-blockers-all"
            className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:text-indigo-200"
          >
            Open Needs a push <ArrowRight size={11} weight="bold" />
          </Link>
        </div>
        {!loading && rows.length === 0 && (
          <div data-testid="dash-blockers-clear" className="mt-3 flex items-center gap-2 text-sm text-emerald-200">
            <CheckCircle size={16} weight="fill" className="text-emerald-400" /> Nothing is blocking any project right now.
          </div>
        )}
        {rows.length > 0 && (
          <ul className="mt-3 divide-y divide-zinc-800/80">
            {rows.slice(0, 3).map(({ p, blocks }) => (
              <li key={p.id} data-testid={`dash-blocker-${p.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <Link to={`/projects/${p.id}`} data-testid={`dash-blocker-link-${p.id}`} className="min-w-0 truncate text-sm font-semibold text-zinc-100 hover:text-indigo-200">
                  {p.name || p.code}
                </Link>
                <InstallBadge p={p} />
                <Link
                  to={stepHref(p, blocks[0])}
                  data-testid={`dash-blocker-step-${p.id}-${blocks[0].key}`}
                  className="min-w-0 flex-1 truncate text-xs text-red-300 hover:text-red-200 sm:text-right"
                >
                  {blocks[0].text}
                  {blocks.length > 1 ? ` · +${blocks.length - 1}` : ""}
                </Link>
              </li>
            ))}
          </ul>
        )}
        {rows.length > 3 && (
          <Link to="/needs-a-push" data-testid="dash-blockers-more" className="mt-2 inline-block text-[11px] text-zinc-500 hover:text-indigo-200">
            +{rows.length - 3} more project{rows.length - 3 === 1 ? "" : "s"} waiting
          </Link>
        )}
      </section>
    );
  }

  return (
    <section
      data-testid="dash-blockers"
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Lightning size={18} weight="fill" className="text-amber-300" />
        <h2 className="text-base font-bold text-zinc-100 md:text-lg">Needs a push</h2>
        <span
          data-testid="dash-blockers-summary"
          className="text-xs text-zinc-500"
        >
          {loading
            ? "Checking projects…"
            : rows.length
              ? `${total} blocker${total === 1 ? "" : "s"} across ${rows.length} project${rows.length === 1 ? "" : "s"}`
              : "Every active project is moving"}
        </span>
        <Link
          to="/projects?risk=risk"
          data-testid="dash-blockers-all"
          className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:text-indigo-200"
        >
          All projects <ArrowRight size={11} weight="bold" />
        </Link>
      </div>

      {!loading && rows.length === 0 && (
        <div
          data-testid="dash-blockers-clear"
          className="mt-4 flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2.5 text-sm text-emerald-200"
        >
          <CheckCircle size={16} weight="fill" className="text-emerald-400" />
          Nothing is blocking any project right now.
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {visible.map(({ p, blocks, warns }) => (
            <article
              key={p.id}
              data-testid={`dash-blocker-${p.id}`}
              className="min-w-0 rounded-md border border-zinc-800 bg-zinc-950 p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/projects/${p.id}`}
                  data-testid={`dash-blocker-link-${p.id}`}
                  className="min-w-0 truncate text-sm font-semibold text-zinc-100 hover:text-indigo-200"
                >
                  {p.name || p.code}
                </Link>
                <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                  {p.code} · {p.stage}
                </span>
                <InstallBadge p={p} className="ml-auto" />
                <HoldBadge status={p.hold_status} since={p.hold_since} note={p.hold_note} testId={`dash-blocker-hold-${p.id}`} />
              </div>
              {p.customer_name && (
                <div className="mt-0.5 truncate text-xs text-zinc-500">{p.customer_name}</div>
              )}
              <ul className="mt-2 space-y-1.5">
                {blocks.slice(0, 3).map((s) => (
                  <li key={s.key}>
                    <Link
                      to={stepHref(p, s)}
                      data-testid={`dash-blocker-step-${p.id}-${s.key}`}
                      className="group flex items-center gap-2 rounded-md border border-red-500/20 bg-red-500/[0.06] px-2.5 py-1.5 text-xs text-zinc-200 transition-colors duration-150 hover:border-red-400/40 hover:bg-red-500/10"
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                      <span className="min-w-0 flex-1 truncate">{s.text}</span>
                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-red-300 group-hover:text-red-200">
                        {s.cta}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              {(blocks.length > 3 || warns > 0) && (
                <div className="mt-2 text-[11px] text-zinc-500">
                  {blocks.length > 3 ? `+${blocks.length - 3} more blocking` : ""}
                  {blocks.length > 3 && warns > 0 ? " · " : ""}
                  {warns > 0 ? `${warns} to watch` : ""}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      {rows.length > SHOW && !full && (
        <button
          type="button"
          data-testid="dash-blockers-more"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 inline-flex h-9 items-center rounded-full border border-zinc-700 px-4 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
        >
          {showAll ? "Show fewer" : `Show ${rows.length - SHOW} more project${rows.length - SHOW === 1 ? "" : "s"}`}
        </button>
      )}
    </section>
  );
};
